import React, { useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import Navigation from "./Navigation";
import Legal from "./Legal";
import styles from "./ProductFeedback.module.scss";
import { useTrackPageVisit } from "../hooks/useTrackPageVisit";
import { config } from "../config";

const PRODUCT_FEEDBACK_API_PATH = "/api/web_interactions/record_product_feedback";

const PRODUCT_DISPLAY_LABELS: Record<string, string> = {
  battery_innovative_product: "Battery Set and Forget",
};

function useProductFeedbackParams(): {
  accountId: string | undefined;
  productName: string | null;
  insightFromDate: string | null;
  positive: string | null;
  allPresent: boolean;
} {
  const { product, accountId, from_insight_date } = useParams<{
    product: string;
    accountId: string;
    from_insight_date: string;
  }>();
  const [searchParams] = useSearchParams();
  const positive = searchParams.get("positive");

  const productName = product?.trim() ?? null;
  const insightFromDate = from_insight_date?.trim() ?? null;
  const positiveTrimmed = positive?.trim() ?? null;

  const allPresent = Boolean(
    accountId &&
      productName &&
      insightFromDate &&
      positiveTrimmed
  );

  return {
    accountId: accountId ?? undefined,
    productName,
    insightFromDate,
    positive: positiveTrimmed,
    allPresent,
  };
}

export default function ProductFeedback() {
  const {
    accountId,
    productName,
    insightFromDate,
    positive,
    allPresent,
  } = useProductFeedbackParams();
  const [comments, setComments] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useTrackPageVisit(
    `${productName}_feedback`,
    allPresent && accountId ? accountId : "",
    {
    type: "product_feedback",
    product: productName ?? undefined,
    insight_from_date: insightFromDate ?? undefined,
    positive: positive ?? undefined,
  });

  function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!allPresent || !accountId || !productName || !insightFromDate || !positive) {
      return;
    }
    setSubmitting(true);
    const payload = {
      account_identifier: accountId,
      product: productName,
      insight_from_date: insightFromDate,
      positive: positive === "true",
      additional_feedback: comments.trim(),
    };
    axios
      .post(config.apiURL + PRODUCT_FEEDBACK_API_PATH, payload)
      .then(() => setSubmitted(true))
      .finally(() => setSubmitting(false));
  }

  const missingMessage =
    "Some required information is missing. Please use the link you received to access this page.";

  return (
    <div className={styles.productFeedback}>
      <Navigation />
      <div className={styles.pageContent}>
        {!allPresent ? (
          <p className={styles.missingParams}>{missingMessage}</p>
        ) : submitted ? (
          <p className={styles.successMessage}>
            Thanks for the additional feedback!
          </p>
        ) : (
          <>
            <h2 className={styles.heading}>
              Thank you for letting us know how you feel about the ENGIE{" "}
              {PRODUCT_DISPLAY_LABELS[productName ?? ""] ?? productName ?? ""} trial, if you have any comments
              please let us know below:
            </h2>
            <form onSubmit={handleSubmit} className={styles.form}>
              <textarea
                className={styles.textarea}
                value={comments}
                onChange={(event) => setComments(event.target.value)}
                rows={6}
                aria-label="Additional comments"
              />
              <button
                type="submit"
                className={styles.submitButton}
                disabled={submitting || !comments.trim()}
              >
                {submitting ? "Submitting…" : "Submit"}
              </button>
            </form>
          </>
        )}
      </div>
      <Legal accountId={accountId} />
    </div>
  );
}
